import { useState } from 'react';
import { Alert, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { IOpoTax } from '@nkr/models/opo-tax';
import useOpoTaxes from '@nkr/store/opo/hooks/use-opo-taxes';
import { SettingsPageWrapper } from '../styles';
import { SettingsTableCard } from '../table-styles';
import ConfirmModal from '../confirm-modal';
import RateInput, { formatRate, validateRate } from './rate-input';

const TaxSettings = () => {
    const { data, loading, error, save, remove } = useOpoTaxes();
    const [newType, setNewType] = useState('');
    const [newRate, setNewRate] = useState('');
    const [addErrors, setAddErrors] = useState<{ type?: string, rate?: string }>({});
    const [editing, setEditing] = useState<{ id: number, rate: string, error?: string } | null>(null);
    const [deleting, setDeleting] = useState<IOpoTax | null>(null);
    const [actionError, setActionError] = useState('');

    if (loading) {
        return null;
    }

    const usedTypes = data ? data.taxes.map(x => x.lineItemType) : [];
    const availableTypes = data ? data.lineItemTypes.filter(x => usedTypes.indexOf(x.id) < 0) : [];

    const handleAdd = async () => {
        const errors = {
            type: newType == '' ? 'Select a line item' : undefined,
            rate: validateRate(newRate)
        };

        setAddErrors(errors);
        setActionError('');

        if (errors.type || errors.rate) {
            return;
        }

        const result = await save(null, { lineItemType: Number(newType), taxPercent: Number(newRate) });

        if (result.success) {
            setNewType('');
            setNewRate('');
            toast.success('Tax rate added');
        } else {
            setActionError(result.message);
        }
    };

    const handleUpdate = async (tax: IOpoTax) => {
        const rateError = validateRate(editing.rate);

        if (rateError) {
            setEditing({ ...editing, error: rateError });
            return;
        }

        setActionError('');

        const result = await save(tax.id, { lineItemType: tax.lineItemType, taxPercent: Number(editing.rate) });

        if (result.success) {
            setEditing(null);
            toast.success('Tax rate saved');
        } else {
            setActionError(result.message);
        }
    };

    const handleDelete = () => {
        const tax = deleting;

        setActionError('');
        setDeleting(null);

        remove(tax.id).then(result => {
            if (result.success) {
                toast.success(`${tax.description} tax rate deleted`);
            } else {
                setActionError(result.message);
            }
        });
    };

    return (
        <SettingsPageWrapper style={{ maxWidth: 900 }}>
            {(error || actionError) && (
                <Alert bsStyle='danger' onDismiss={actionError ? () => setActionError('') : undefined}>{error || actionError}</Alert>
            )}

            {data && (
                <SettingsTableCard>
                    <div className='table-card-header'>
                        <h4>Taxable Line Items</h4>
                    </div>

                    <div className='table-card-body'>
                        <Table condensed>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left' }}>Taxable Line Item</th>
                                    <th style={{ textAlign: 'left', width: 220 }}>Tax Rate</th>
                                    <th style={{ width: 160 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.taxes.map(tax => {
                                    const isEditing = editing?.id == tax.id;

                                    return (
                                        <tr key={tax.id}>
                                            <td style={{ textAlign: 'left' }}>{tax.description}</td>
                                            <td style={{ textAlign: 'left' }}>
                                                {isEditing
                                                    ? <RateInput value={editing.rate} error={editing.error} autoFocus onChange={rate => setEditing({ id: tax.id, rate })} onEnter={() => handleUpdate(tax)} />
                                                    : formatRate(tax.taxPercent)}
                                            </td>
                                            <td>
                                                <div className='btn-group'>
                                                    {isEditing
                                                        ? <>
                                                            <button type='button' className='btn btn-primary' onClick={() => handleUpdate(tax)}>Save</button>
                                                            <button type='button' className='btn btn-default' onClick={() => setEditing(null)}>Cancel</button>
                                                        </>
                                                        : <>
                                                            <button type='button' className='btn btn-default' disabled={editing != null} onClick={() => setEditing({ id: tax.id, rate: String(Number(tax.taxPercent)) })}>Edit</button>
                                                            <button type='button' className='btn btn-default' disabled={editing != null} onClick={() => setDeleting(tax)}>Delete</button>
                                                        </>}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}

                                {data.taxes.length == 0 && (
                                    <tr>
                                        <td colSpan={3}>No tax rates have been added yet</td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>

                    {availableTypes.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 20px', borderTop: '1px solid #EEF1F5' }}>
                            <div className={addErrors.type ? 'has-error' : undefined} style={{ flex: '1 1 auto' }}>
                                <select className='form-control' aria-label='Taxable line item' value={newType} onChange={e => setNewType(e.currentTarget.value)}>
                                    <option value=''>Select a line item...</option>
                                    {availableTypes.map(type =>
                                        <option key={type.id} value={type.id}>{type.description}</option>
                                    )}
                                </select>
                                {addErrors.type && <div className='help-block'>{addErrors.type}</div>}
                            </div>
                            <div style={{ width: 200 }}>
                                <RateInput value={newRate} error={addErrors.rate} onChange={setNewRate} onEnter={handleAdd} />
                            </div>
                            <div style={{ width: 160, textAlign: 'right' }}>
                                <button type='button' className='btn btn-primary' style={{ height: 32 }} onClick={handleAdd}>Add Tax Rate</button>
                            </div>
                        </div>
                    )}
                </SettingsTableCard>
            )}

            <ConfirmModal
                show={deleting != null}
                title='Delete Tax Rate'
                message={<>Delete the <strong>{deleting ? formatRate(deleting.taxPercent) : ''}</strong> tax rate for <strong>{deleting?.description}</strong>?</>}
                confirmText='Delete'
                onConfirm={handleDelete}
                onCancel={() => setDeleting(null)}
            />
        </SettingsPageWrapper>
    );
}

export default TaxSettings;
